import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Skeleton,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
// components
import { useSnackbar } from '../../components/snackbar';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { fetchOrderById, updateOrderStatus } from '../../redux/features/order/orderSlice';
// auth
import { useAuthContext } from '../../auth/useAuthContext';
// types
import { OrderResponse } from '../../@types/order/order.type';
import { OrderState } from '../../@enums/orderState';
// hooks
import { useProductTranslation } from '../../hooks/useProductTranslation';
// routes
import { PATH_AGENT } from '../../routes/paths';
// components
import Iconify from '../../components/iconify';
// sections
import AiTranscriptAnalysis from '../dashboard/orders/AiTranscriptAnalysis';

// Status color mapping
const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  WAITING: { color: '#f59e0b', bg: '#fef3c7', icon: 'mdi:clock-outline', label: 'order.waiting' },
  ACCEPTED: { color: '#10b981', bg: '#d1fae5', icon: 'mdi:check-circle-outline', label: 'order.accepted' },
  IN_PROGRESS: { color: '#3b82f6', bg: '#dbeafe', icon: 'mdi:progress-clock', label: 'order.on_the_move' },
  POS_ENTERED: { color: '#8b5cf6', bg: '#ede9fe', icon: 'mdi:check-decagram', label: 'nav.Pos_config' },
  DELIVERED: { color: '#059669', bg: '#a7f3d0', icon: 'mdi:truck-check', label: 'order.delivered' },
  ON_THE_MOVE: { color: '#0891b2', bg: '#cffafe', icon: 'mdi:motorbike', label: 'order.on_the_move' },
  UNACCEPTED: { color: '#ef4444', bg: '#fee2e2', icon: 'mdi:close-circle-outline', label: 'order.unaccepted' },
  UNDELIVERED: { color: '#dc2626', bg: '#fecaca', icon: 'mdi:truck-remove', label: 'order.undelivered' },
  CANCELLED: { color: '#ef4444', bg: '#fee2e2', icon: 'mdi:cancel', label: 'order.cancelled' },
};

function getStatusConfig(state: string, t: any) {
  const cfg = STATUS_CONFIG[state] || { color: '#6b7280', bg: '#f3f4f6', icon: 'mdi:help-circle', label: state };
  return { ...cfg, label: t(cfg.label) };
}

function fCurrency(value: number) {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
}

function formatDateTime(date: Date) {
  return new Intl.DateTimeFormat('fr-CA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(date));
}

// ----------------------------------------------------------------------

export default function AgentOrderDetailPage() {
  const theme = useTheme();
  const { orderId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getName } = useProductTranslation();
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [errorPopup, setErrorPopup] = useState<string | null>(null);
  const { user } = useAuthContext();
  const [currentTab, setCurrentTab] = useState('details');

  const loading = useSelector((s: any) => s.orders.single.loading);
  const data = useSelector((s: any) => s.orders.single.data) as OrderResponse | null;
  const updateLoading = useSelector((s: any) => s.orders.updateStatus.loading);

  useEffect(() => {
    if (orderId) {
      dispatch(fetchOrderById(orderId));
    }
  }, [dispatch, orderId]);

  const { subtotal, total, federalTax, provincialTax } = useMemo(() => {
    if (!data) return { subtotal: 0, total: 0, federalTax: 0, provincialTax: 0 };

    const sb = (data.orderedProducts || []).reduce((acc, item) => {
      let base = 0;
      if (item.unitPrice != null) {
        base = Number(item.unitPrice);
      } else if (item.trioSelections && item.trioSelections.length > 0) {
        base = Number(item.product.trioPrice || 0);
      } else {
        base = Number(item.product.price || 0);
      }
      const extras = (item.trioSelections || []).reduce((s, sel) => s + Number(sel.price || 0), 0);
      return acc + (base + extras) * Number(item.quantity || 0);
    }, 0);

    const fedRate = data.federalTaxRate ?? 5.0;
    const provRate = data.provincialTaxRate ?? 9.975;
    const fedTax = sb * (fedRate / 100);
    const provTax = sb * (provRate / 100);

    return { subtotal: sb, federalTax: fedTax, provincialTax: provTax, total: sb + fedTax + provTax };
  }, [data]);

  const handleStatusChange = async (newStatus: OrderState) => {
    if (data) {
      try {
        const resultAction = await dispatch(updateOrderStatus({ id: data.id, orderState: newStatus }));
        if (updateOrderStatus.fulfilled.match(resultAction)) {
          enqueueSnackbar(t('org.update_success'), { variant: 'success' });
          navigate(PATH_AGENT.root);
        } else {
          setErrorPopup((resultAction.payload as string) || t('org.update_fail'));
        }
      } catch (error) {
        setErrorPopup(t('org.error'));
      }
    }
  };

  const statusCfg = data ? getStatusConfig(data.orderState, t) : null;

  return (
    <>
      <Helmet>
        <title>Commande #{orderId} | Agent Drive-Thru</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Back Button + Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Button
            id="back-to-orders"
            onClick={() => navigate(PATH_AGENT.root)}
            startIcon={<Iconify icon="mdi:arrow-left" />}
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.08) },
            }}
          >
            {t('nav.back')}
          </Button>

          {data && statusCfg && (
            <Chip
              icon={<Iconify icon={statusCfg.icon} width={18} />}
              label={statusCfg.label}
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                bgcolor: statusCfg.bg,
                color: statusCfg.color,
                height: 36,
                borderRadius: 2,
                '& .MuiChip-icon': { color: statusCfg.color },
              }}
            />
          )}

          {data?.humanTakeover && (
            <Chip
              icon={<Iconify icon="mdi:account-alert" width={18} />}
              label="Intervention humaine demandée"
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                bgcolor: '#fef3c7',
                color: '#d97706',
                height: 36,
                borderRadius: 2,
                border: '1px solid #f59e0b',
                '& .MuiChip-icon': { color: '#d97706' },
              }}
            />
          )}
        </Stack>

        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab value="details" label="Détails de la commande" />
          {user?.role === 'SUPER_ADMIN' && (
            <Tab value="analysis" label="Analyse IA & Transcript" />
          )}
        </Tabs>

        {currentTab === 'details' && (
        <Grid container spacing={3}>
          {/* Left Column - Order Summary */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              {loading || !data ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h4" fontWeight={800}>
                      {t('order.id')} #{data.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formatDateTime(data.createdAt)}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Totals */}
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {t('order.subtotal')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {fCurrency(subtotal)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {t('order.tax_fed')} ({data.federalTaxRate ?? 5}%)
                      </Typography>
                      <Typography variant="body2">{fCurrency(federalTax)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {t('order.tax_prov')} ({data.provincialTaxRate ?? 9.975}%)
                      </Typography>
                      <Typography variant="body2">{fCurrency(provincialTax)}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" fontWeight={800}>
                        {t('order.total')}
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="primary.main">
                        {fCurrency(total)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider />

                  {/* Action Buttons */}
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {t('order.status')}
                    </Typography>

                    {data.humanTakeover && data.orderState === OrderState.WAITING && (
                      <LoadingButton
                        id="btn-take-over"
                        fullWidth
                        size="large"
                        variant="contained"
                        loading={updateLoading}
                        onClick={() => handleStatusChange(OrderState.IN_PROGRESS)}
                        startIcon={<Iconify icon="mdi:account-check" />}
                        sx={{
                          height: 56,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          bgcolor: '#f59e0b',
                          '&:hover': { bgcolor: '#d97706' },
                        }}
                      >
                        Prendre en charge
                      </LoadingButton>
                    )}

                    <LoadingButton
                      id="btn-in-progress"
                      fullWidth
                      size="large"
                      variant={data.orderState === OrderState.IN_PROGRESS ? 'contained' : 'outlined'}
                      loading={updateLoading}
                      onClick={() => handleStatusChange(OrderState.IN_PROGRESS)}
                      disabled={data.orderState === OrderState.IN_PROGRESS}
                      startIcon={<Iconify icon="mdi:progress-clock" />}
                      sx={{
                        height: 56,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        ...(data.orderState !== OrderState.IN_PROGRESS && {
                          borderColor: '#3b82f6',
                          color: '#3b82f6',
                          '&:hover': {
                            borderColor: '#2563eb',
                            bgcolor: alpha('#3b82f6', 0.08),
                          },
                        }),
                        ...(data.orderState === OrderState.IN_PROGRESS && {
                          bgcolor: '#3b82f6',
                          '&:hover': { bgcolor: '#2563eb' },
                          '&.Mui-disabled': {
                            bgcolor: alpha('#3b82f6', 0.5),
                            color: '#fff',
                          },
                        }),
                      }}
                    >
                      {t('order.on_the_move')}
                    </LoadingButton>

                    <LoadingButton
                      id="btn-pos-entered"
                      fullWidth
                      size="large"
                      variant={data.orderState === OrderState.POS_ENTERED ? 'contained' : 'outlined'}
                      loading={updateLoading}
                      onClick={() => handleStatusChange(OrderState.POS_ENTERED)}
                      disabled={data.orderState === OrderState.POS_ENTERED}
                      startIcon={<Iconify icon="mdi:check-decagram" />}
                      sx={{
                        height: 56,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        ...(data.orderState !== OrderState.POS_ENTERED && {
                          borderColor: '#8b5cf6',
                          color: '#8b5cf6',
                          '&:hover': {
                            borderColor: '#7c3aed',
                            bgcolor: alpha('#8b5cf6', 0.08),
                          },
                        }),
                        ...(data.orderState === OrderState.POS_ENTERED && {
                          bgcolor: '#8b5cf6',
                          '&:hover': { bgcolor: '#7c3aed' },
                          '&.Mui-disabled': {
                            bgcolor: alpha('#8b5cf6', 0.5),
                            color: '#fff',
                          },
                        }),
                      }}
                    >
                      {t('nav.Pos_config')}
                    </LoadingButton>

                    <Divider sx={{ my: 0.5 }} />

                    <LoadingButton
                      id="btn-cancel-order"
                      fullWidth
                      size="large"
                      variant={data.orderState === OrderState.CANCELLED ? 'contained' : 'outlined'}
                      loading={updateLoading}
                      onClick={() => setOpenCancelConfirm(true)}
                      disabled={data.orderState === OrderState.CANCELLED}
                      startIcon={<Iconify icon="mdi:cancel" />}
                      sx={{
                        height: 56,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        ...(data.orderState !== OrderState.CANCELLED && {
                          borderColor: '#ef4444',
                          color: '#ef4444',
                          '&:hover': {
                            borderColor: '#dc2626',
                            bgcolor: alpha('#ef4444', 0.08),
                          },
                        }),
                        ...(data.orderState === OrderState.CANCELLED && {
                          bgcolor: '#6b7280',
                          '&:hover': { bgcolor: '#4b5563' },
                          '&.Mui-disabled': {
                            bgcolor: alpha('#6b7280', 0.5),
                            color: '#fff',
                          },
                        }),
                      }}
                    >
                      {t('nav.cancel')}
                    </LoadingButton>
                  </Stack>
                </Stack>
              )}
            </Card>
          </Grid>

          {/* Right Column - Products */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify
                    icon="mdi:food-outline"
                    width={24}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t('order.products_list')}
                  </Typography>
                  {data && (
                    <Chip
                      label={t('order.products_count', { count: (data.orderedProducts || []).length })}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                      }}
                    />
                  )}
                </Stack>
              </Box>

              <Box sx={{ p: 2 }}>
                {loading || !data ? (
                  <Stack spacing={1.5}>
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    {data.orderedProducts.map((item) => {
                      const isTrio = !!(item.trioSelections && item.trioSelections.length > 0);
                      let base = 0;
                      if (item.unitPrice != null) {
                        base = Number(item.unitPrice);
                      } else if (isTrio) {
                        base = Number(item.product.trioPrice || 0);
                      } else {
                        base = Number(item.product.price || 0);
                      }
                      const extras = (item.trioSelections || []).reduce(
                        (s, sel) => s + Number(sel.price || 0),
                        0
                      );
                      const line = (base + extras) * Number(item.quantity || 0);

                      return (
                        <Box
                          key={item.id}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.grey[500], 0.04),
                            border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                            transition: 'all 0.15s',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.grey[500], 0.08),
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle1" fontWeight={700}>
                                  {getName(item.product.translations)}
                                </Typography>
                                {isTrio && (
                                  <Chip
                                    label="Trio"
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      bgcolor: alpha('#ef4444', 0.1),
                                      color: '#ef4444',
                                      height: 22,
                                    }}
                                  />
                                )}
                              </Stack>

                              <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('order.qty_short')}: <strong>{item.quantity}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t('order.price_short')}: <strong>{fCurrency(base)}</strong>
                                </Typography>
                                {extras > 0 && (
                                  <Typography variant="body2" color="text.secondary">
                                    {t('order.extras')}: <strong>{fCurrency(extras)}</strong>
                                  </Typography>
                                )}
                              </Stack>

                              {/* Trio Selections / Modifiers */}
                              {isTrio && (item.trioSelections || []).length > 0 && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  flexWrap="wrap"
                                  sx={{ mt: 1, gap: 0.5 }}
                                >
                                  {(item.trioSelections || []).map((sel) => (
                                    <Chip
                                      key={sel.id}
                                      label={`${sel.name} (+${fCurrency(Number(sel.price || 0))})`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderColor: alpha('#8b5cf6', 0.3),
                                        color: '#7c3aed',
                                        bgcolor: alpha('#8b5cf6', 0.04),
                                      }}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </Box>

                            <Typography variant="subtitle1" fontWeight={800} sx={{ minWidth: 80, textAlign: 'right' }}>
                              {fCurrency(line)}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
        )}

        {currentTab === 'analysis' && user?.role === 'SUPER_ADMIN' && (
          <AiTranscriptAnalysis steps={data?.steps || []} />
        )}
      </Container>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={openCancelConfirm}
        onClose={() => setOpenCancelConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('nav.cancel')} ?</DialogTitle>
        <DialogContent sx={{ typography: 'body2', color: 'text.secondary' }}>
          {t('dialogs.order_deletion_confirmation')} #{data?.id} ? {t('dialogs.confirm_deletion')}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setOpenCancelConfirm(false)}>
            {t('nav.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={updateLoading}
            onClick={() => {
              setOpenCancelConfirm(false);
              handleStatusChange(OrderState.CANCELLED);
            }}
          >
            {t('order.confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* POS Sync Error Dialog */}
      <Dialog
        open={!!errorPopup}
        onClose={() => setErrorPopup(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Iconify icon="mdi:alert-circle" width={24} />
          Erreur de synchronisation
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {errorPopup}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setErrorPopup(null)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
